import React, {useState, useCallback, useMemo} from 'react';
import {
  Calendar as SpectrumCalendar,
  ActionButton,
  Flex,
  Text,
} from '@adobe/react-spectrum';
import {CalendarDate, today, getLocalTimeZone} from '@internationalized/date';
import {ChevronLeft, ChevronRight} from 'lucide-react';

const YEARS_PER_PAGE = 12;

const toIntlLocale = locale => locale === 'jp-JP' ? 'ja-JP' : locale || 'en-GB';

export function EnhancedSingleCalendar({value, onChange, locale}) {
  const [viewMode, setViewMode] = useState('days');

  const defaultDate = useMemo(() => value || today(getLocalTimeZone()), [value]);
  const [focusedValue, setFocusedValue] = useState(defaultDate);
  const [displayYear, setDisplayYear] = useState(defaultDate.year);
  const [yearPageStart, setYearPageStart] = useState(() => {
    const y = defaultDate.year;
    return y - (y % YEARS_PER_PAGE);
  });

  const handleFocusChange = useCallback((date) => {
    setFocusedValue(date);
    setDisplayYear(date.year);
  }, []);

  const monthName = useMemo(() => {
    const d = new Date(focusedValue.year, focusedValue.month - 1);
    return d.toLocaleString(toIntlLocale(locale), {month: 'long'});
  }, [focusedValue.year, focusedValue.month, locale]);

  const months = useMemo(() => (
    Array.from({length: 12}, (_, index) => new Date(2000, index, 1)
      .toLocaleString(toIntlLocale(locale), {month: 'short'})
  )), [locale]);

  const handlePrev = useCallback(() => {
    if (viewMode === 'days') {
      setFocusedValue(prev => {
        const next = prev.subtract({months: 1});
        setDisplayYear(next.year);
        return next;
      });
    } else if (viewMode === 'months') {
      setDisplayYear(prev => prev - 1);
    } else {
      setYearPageStart(prev => prev - YEARS_PER_PAGE);
    }
  }, [viewMode]);

  const handleNext = useCallback(() => {
    if (viewMode === 'days') {
      setFocusedValue(prev => {
        const next = prev.add({months: 1});
        setDisplayYear(next.year);
        return next;
      });
    } else if (viewMode === 'months') {
      setDisplayYear(prev => prev + 1);
    } else {
      setYearPageStart(prev => prev + YEARS_PER_PAGE);
    }
  }, [viewMode]);

  const handleYearSelect = useCallback((year) => {
    setDisplayYear(year);
    setViewMode('months');
  }, []);

  const handleMonthSelect = useCallback((month) => {
    const newDate = new CalendarDate(displayYear, month, 1);
    setFocusedValue(newDate);
    setDisplayYear(displayYear);
    setViewMode('days');
  }, [displayYear]);

  const openMonthsView = useCallback(() => {
    setDisplayYear(focusedValue.year);
    setViewMode('months');
  }, [focusedValue.year]);

  const openYearsView = useCallback(() => {
    const y = viewMode === 'months' ? displayYear : focusedValue.year;
    setYearPageStart(y - (y % YEARS_PER_PAGE));
    setViewMode('years');
  }, [viewMode, displayYear, focusedValue.year]);

  const renderHeaderTitle = () => {
    if (viewMode === 'days') {
      return (
        <Flex flex={1} justifyContent="center" gap="size-50">
          <button type="button" className="enhanced-rc-title-btn" onClick={openMonthsView}>
            {monthName}
          </button>
          <button type="button" className="enhanced-rc-title-btn" onClick={openYearsView}>
            {focusedValue.year}
          </button>
        </Flex>
      );
    }
    if (viewMode === 'months') {
      return (
        <Flex flex={1} justifyContent="center">
          <button type="button" className="enhanced-rc-title-btn" onClick={openYearsView}>
            {displayYear}
          </button>
        </Flex>
      );
    }
    return (
      <Flex flex={1} justifyContent="center">
        <Text UNSAFE_className="enhanced-rc-title-text">
          {yearPageStart} – {yearPageStart + YEARS_PER_PAGE - 1}
        </Text>
      </Flex>
    );
  };

  return (
    <div className="enhanced-rc-wrapper">
      <Flex alignItems="center" marginBottom="size-200" marginStart="size-50" marginEnd="size-50">
        <ActionButton isQuiet onPress={handlePrev}>
          <ChevronLeft size={22} />
        </ActionButton>
        {renderHeaderTitle()}
        <ActionButton isQuiet onPress={handleNext}>
          <ChevronRight size={22} />
        </ActionButton>
      </Flex>

      <div className="enhanced-rc-body">
        <div style={{display: viewMode === 'days' ? undefined : 'none'}}>
          <SpectrumCalendar
            value={value}
            onChange={onChange}
            focusedValue={focusedValue}
            onFocusChange={handleFocusChange}
            UNSAFE_className="enhanced-rc-cal"
          />
        </div>

        {viewMode === 'months' && (
          <div className="enhanced-rc-grid enhanced-rc-months-grid">
            {months.map((name, i) => (
              <ActionButton
                key={i}
                isQuiet
                onPress={() => handleMonthSelect(i + 1)}
                UNSAFE_className="enhanced-rc-grid-cell"
              >
                {name}
              </ActionButton>
            ))}
          </div>
        )}

        {viewMode === 'years' && (
          <div className="enhanced-rc-grid enhanced-rc-years-grid">
            {Array.from({length: YEARS_PER_PAGE}, (_, i) => {
              const year = yearPageStart + i;
              return (
                <ActionButton
                  key={year}
                  isQuiet
                  onPress={() => handleYearSelect(year)}
                  UNSAFE_className="enhanced-rc-grid-cell"
                >
                  {year}
                </ActionButton>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
